import type { FaceKeyframe, SceneChunk, SceneFace, SceneMetadata } from "../schemas"

export type LayoutMode = SceneChunk["mode"]
export type TransitionType = "cut" | "crossfade" | "slide" | "zoom"

/** A scene shorter than this keeps the previous scene's layout, so the frame doesn't flip-flop. */
export const MIN_LAYOUT_HOLD_SEC = 1.5
/** Cross-dissolve length used when the layout changes at a boundary that has no explicit transition. */
export const LAYOUT_CROSSFADE_SEC = 0.3
/** Length of an explicit transition when neither the boundary nor the clip sets one. */
export const DEFAULT_TRANSITION_SEC = 0.4

export const TRANSITION_TYPES: TransitionType[] = ["cut", "crossfade", "slide", "zoom"]

const DEFAULT_FACE: SceneFace = { x_pct: 0.5, y_pct: 0.4, w_pct: 0.08, h_pct: 0.15 }

function face(x: number): SceneFace {
  return { ...DEFAULT_FACE, x_pct: Math.max(0, Math.min(1, x)) }
}

/**
 * A face standing in for a person we know nothing about: centred on `x` and sized so its panel
 * crops a `cropWidth` slice of the source (the panel shows 2.5 face widths).
 */
function sliceFace(x: number, cropWidth: number): SceneFace {
  return { ...DEFAULT_FACE, x_pct: x, w_pct: cropWidth / 2.5 }
}

/** Maps the legacy enum values (none/fade/wipe) onto the current transition set. */
export function normalizeTransition(value: string | undefined | null): TransitionType | null {
  switch (value) {
    case "cut":
    case "none":
      return "cut"
    case "crossfade":
    case "fade":
      return "crossfade"
    case "slide":
    case "wipe":
      return "slide"
    case "zoom":
      return "zoom"
    default:
      return null
  }
}

/**
 * Returns the chunk switched to `mode`, filling in face positions the new layout needs
 * from what the chunk already knows (its faces or its single-face center).
 */
export function withLayoutMode(chunk: SceneChunk, mode: LayoutMode): SceneChunk {
  if (chunk.mode === mode) return chunk
  const faces = chunk.faces ?? []
  const cx = chunk.face_center_x_pct ?? (faces.length ? faces[0].x_pct : 0.5)
  const updated: SceneChunk = { ...chunk, mode }
  // No detected face at all (e.g. a clip without scenes): frame slices of the source, not guesses.
  const unknown = !faces.length && !chunk.face_count

  switch (mode) {
    case "single":
      updated.face_center_x_pct = faces.length >= 2
        ? (faces[0].x_pct + faces[1].x_pct) / 2
        : faces[0]?.x_pct ?? chunk.face_center_x_pct ?? 0.5
      break
    case "dual":
      if (unknown) updated.faces = [sliceFace(0.25, 0.5), sliceFace(0.75, 0.5)]
      else if (faces.length < 2) updated.faces = [face(cx - 0.2), face(cx + 0.2)]
      break
    case "triple":
      if (faces.length >= 3) {
        updated.faces = faces.slice(0, 3)
      } else if (faces.length === 2) {
        // The missing third person usually sits between the two detected faces.
        updated.faces = [faces[0], faces[1], {
          ...DEFAULT_FACE,
          x_pct: (faces[0].x_pct + faces[1].x_pct) / 2,
          y_pct: (faces[0].y_pct + faces[1].y_pct) / 2,
        }]
      } else if (unknown) {
        // Thirds of the frame: middle on top, left and right below.
        updated.faces = [sliceFace(3 / 6, 1 / 3), sliceFace(1 / 6, 1 / 3), sliceFace(5 / 6, 1 / 3)]
      } else {
        updated.faces = [face(cx), face(cx - 0.25), face(cx + 0.25)]
      }
      break
    case "quad": {
      const spread = [0.2, 0.4, 0.6, 0.8].map(face)
      updated.faces = [0, 1, 2, 3].map((i) => faces[i] ?? spread[i])
      break
    }
    case "screenshare":
      if (!faces.length) updated.faces = [face(cx)]
      break
    case "wide":
      break
  }
  return updated
}

/**
 * A clip with no detected scenes as one scene covering all of `source`, framed on `centerXPct`,
 * so it can still take a layout, be split into scenes and get transitions. Keeps `meta`'s other fields.
 */
export function oneSceneMetadata(meta: SceneMetadata | undefined, source: SceneMetadata["source"], centerXPct = 0.5): SceneMetadata {
  return {
    ...meta,
    version: meta?.version ?? "4",
    source,
    chunks: [{
      id: "scene-1",
      start_sec: 0,
      end_sec: source.total_frames / source.fps,
      mode: "single",
      face_count: 0,
      face_center_x_pct: centerXPct,
    }],
  }
}

/** How many people each layout frames; showing fewer duplicates and crops the same face. */
export const LAYOUT_PEOPLE: Record<LayoutMode, number> = { single: 1, dual: 2, triple: 3, quad: 4, screenshare: 1, wide: 0 }

/** True when `mode` needs more people than were detected in the chunk (unknown counts never are). */
export function needsMorePeople(chunk: SceneChunk, mode: LayoutMode): boolean {
  return chunk.face_count > 0 && LAYOUT_PEOPLE[mode] > chunk.face_count
}

/**
 * Picks the layout a chunk renders with: the clip-wide fixed layout where the scene has the people
 * for it, otherwise the chunk's own (Auto). Auto never uses a detected layout that needs more people
 * than the scene has; one the user picked for the scene is kept.
 */
export function effectiveMode(meta: Pick<SceneMetadata, "layout">, chunk: SceneChunk): LayoutMode {
  if (meta.layout && meta.layout !== "auto" && !needsMorePeople(chunk, meta.layout)) return meta.layout
  if (chunk.user_mode || !needsMorePeople(chunk, chunk.mode)) return chunk.mode
  return layoutForPeople(chunk.face_count)
}

/** The layout that frames `count` detected people, one panel each (4+ share the grid). */
export function layoutForPeople(count: number): LayoutMode {
  return (["single", "dual", "triple", "quad"] as const)[Math.max(1, Math.min(count, 4)) - 1]
}

export interface LayoutSpan {
  /** The chunk to render, already switched to its effective layout. */
  chunk: SceneChunk
  startFrame: number
  /** Exclusive. */
  endFrame: number
  /** How the previous span hands over to this one. `frames` is 0 for a hard cut. */
  transitionIn: { type: TransitionType; frames: number }
}

/**
 * Turns scene chunks into frame spans the composition renders one after another.
 *
 * - A fixed clip-wide layout overrides the mode of every chunk with the people for it (effectiveMode).
 * - A chunk shorter than MIN_LAYOUT_HOLD_SEC keeps its neighbour's layout (the previous one, or
 *   the next one for the very first chunk) but still frames its own faces.
 * - The transition into a span is the chunk's own, else the clip default; a layout change with no
 *   transition gets a short crossfade instead of a hard cut. Same-layout boundaries stay hard cuts.
 * - With auto transitions on, a chunk without its own transition gets the clip default (a crossfade when
 *   that is a cut) only where the layout changes, and a hard cut everywhere else.
 */
export function resolveLayoutSpans(meta: SceneMetadata, fps: number, totalFrames: number): LayoutSpan[] {
  const sorted = [...meta.chunks].sort((a, b) => a.start_sec - b.start_sec)
  const defaultType = normalizeTransition(meta.default_transition?.type) ?? "cut"
  const defaultSec = meta.default_transition?.duration_sec ?? DEFAULT_TRANSITION_SEC

  const modes = sorted.map((c) => effectiveMode(meta, c))
  const held = modes.map((mode, i) => {
    const end = sorted[i + 1]?.start_sec ?? sorted[i].end_sec
    if (end - sorted[i].start_sec >= MIN_LAYOUT_HOLD_SEC) return mode
    if (i > 0) return null // resolved below against the previous held mode
    return modes[1] ?? mode
  })
  for (let i = 1; i < held.length; i++) held[i] = held[i] ?? held[i - 1]

  const spans: LayoutSpan[] = []
  sorted.forEach((chunk, i) => {
    const startFrame = i === 0 ? 0 : Math.round(chunk.start_sec * fps)
    const prev = spans[spans.length - 1]
    if (prev && startFrame <= prev.startFrame) return
    if (startFrame >= totalFrames) return
    if (prev) prev.endFrame = startFrame

    let transitionIn: LayoutSpan["transitionIn"] = { type: "cut", frames: 0 }
    if (prev) {
      const layoutChanges = prev.chunk.mode !== held[i]
      const type = normalizeTransition(chunk.transition)
        ?? (!meta.auto_transitions ? defaultType : !layoutChanges ? "cut" : defaultType === "cut" ? "crossfade" : defaultType)
      const sec = meta.auto_transitions && !chunk.transition && defaultType === "cut" ? LAYOUT_CROSSFADE_SEC : defaultSec
      if (type !== "cut") {
        transitionIn = { type, frames: Math.round(sec * fps) }
      } else if (layoutChanges) {
        transitionIn = { type: "crossfade", frames: Math.round(LAYOUT_CROSSFADE_SEC * fps) }
      }
    }
    spans.push({ chunk: withLayoutMode(chunk, held[i]!), startFrame, endFrame: totalFrames, transitionIn })
  })

  // A transition can't outlast either side of the boundary.
  for (let i = 1; i < spans.length; i++) {
    const t = spans[i].transitionIn
    const limit = Math.min(spans[i - 1].endFrame - spans[i - 1].startFrame, spans[i].endFrame - spans[i].startFrame)
    t.frames = Math.max(0, Math.min(t.frames, limit))
    if (t.frames === 0) t.type = "cut"
  }
  return spans
}

/** The chunk as the composition draws it at `frame` (held layout applied), or null past the spans. */
export function layoutChunkAt(spans: LayoutSpan[], frame: number): SceneChunk | null {
  return spans.find((s) => frame >= s.startFrame && frame < s.endFrame)?.chunk ?? null
}

/**
 * Style for the outgoing (frozen) layout drawn over the incoming one, `progress` running 0→1
 * across the transition. Deterministic per frame.
 */
export function outgoingTransitionStyle(type: TransitionType, progress: number): {
  opacity: number
  translateXPct: number
  scale: number
} {
  const p = Math.max(0, Math.min(1, progress))
  const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2
  switch (type) {
    case "crossfade":
      return { opacity: 1 - p, translateXPct: 0, scale: 1 }
    case "slide":
      return { opacity: 1, translateXPct: -100 * eased, scale: 1 }
    case "zoom":
      return { opacity: 1 - p, translateXPct: 0, scale: 1 + 0.25 * eased }
    case "cut":
      return { opacity: 0, translateXPct: 0, scale: 1 }
  }
}

export interface PanelRect {
  /** Fractions of the output frame. */
  x: number
  y: number
  w: number
  h: number
  /** Index into chunk.faces; -1 means the single-mode face_center_x_pct. */
  faceIndex: number
}

/** Size of the screenshare picture-in-picture, as a fraction of the output's shorter side. */
export const SCREENSHARE_PIP_SIZE = 0.4

/**
 * Where each draggable face panel of a layout sits, as fractions of the output frame.
 * Shared by the composition and the editor's drag overlay so they line up.
 */
export function layoutPanelRects(chunk: SceneChunk, outputAspect: number): PanelRect[] {
  switch (chunk.mode) {
    case "single":
      return [{ x: 0, y: 0, w: 1, h: 1, faceIndex: -1 }]
    case "dual": {
      // DualMode puts the left-most face on top.
      const order = (chunk.faces ?? []).map((f, i) => ({ x: f.x_pct, i })).sort((a, b) => a.x - b.x).map((f) => f.i)
      return [
        { x: 0, y: 0, w: 1, h: 0.5, faceIndex: order[0] ?? 0 },
        { x: 0, y: 0.5, w: 1, h: 0.5, faceIndex: order[1] ?? order[0] ?? 1 },
      ]
    }
    case "triple":
      return [
        { x: 0, y: 0, w: 1, h: 0.5, faceIndex: 0 },
        { x: 0, y: 0.5, w: 0.5, h: 0.5, faceIndex: 1 },
        { x: 0.5, y: 0.5, w: 0.5, h: 0.5, faceIndex: 2 },
      ]
    case "quad":
      return [
        { x: 0, y: 0, w: 0.5, h: 0.5, faceIndex: 0 },
        { x: 0.5, y: 0, w: 0.5, h: 0.5, faceIndex: 1 },
        { x: 0, y: 0.5, w: 0.5, h: 0.5, faceIndex: 2 },
        { x: 0.5, y: 0.5, w: 0.5, h: 0.5, faceIndex: 3 },
      ]
    case "screenshare": {
      const shorter = Math.min(1, outputAspect)
      const w = SCREENSHARE_PIP_SIZE * shorter / outputAspect
      const h = SCREENSHARE_PIP_SIZE * shorter
      // Overlap the bottom edge of a centred 16:9 screen in portrait; bottom-right corner otherwise.
      return [{ x: 1 - w - 0.04, y: Math.min(0.58, 1 - h - 0.04), w, h, faceIndex: 0 }]
    }
    case "wide":
      return []
  }
}

// ---------------------------------------------------------------------------
// Face tracks and crop windows (shared by the composition, the editor and the panel check)
// ---------------------------------------------------------------------------

/** A source-pixel rectangle. */
export interface SourceRect {
  x: number
  y: number
  w: number
  h: number
}

/** A track's box at `t` seconds: smoothstep-eased between keyframes, held past either end. */
export function keyframeAt(track: FaceKeyframe[], t: number): SceneFace {
  const i = track.findIndex((k) => k.t >= t)
  const box = i < 0 ? track[track.length - 1] : track[i]
  if (i <= 0) return { x_pct: box.x_pct, y_pct: box.y_pct, w_pct: box.w_pct, h_pct: box.h_pct }
  const a = track[i - 1]
  const p = (t - a.t) / Math.max(box.t - a.t, 1e-6)
  const e = p * p * (3 - 2 * p)
  const mix = (k: "x_pct" | "y_pct" | "w_pct" | "h_pct") => a[k] + (box[k] - a[k]) * e
  return { x_pct: mix("x_pct"), y_pct: mix("y_pct"), w_pct: mix("w_pct"), h_pct: mix("h_pct") }
}

/** Where `face` is at `t` clip seconds: its track when it has one, else its static box. */
export function faceAt(face: SceneFace, t: number): SceneFace {
  return face.track?.length ? keyframeAt(face.track, t) : face
}

/** The single-mode crop centre at `t`: the tracked person, else the static face_center_x_pct. */
export function faceCenterXAt(chunk: SceneChunk, t: number): number {
  return chunk.face_track?.length ? keyframeAt(chunk.face_track, t).x_pct : chunk.face_center_x_pct ?? 0.5
}

/** A face sits this far down its panel, so the head has room above it. */
export const FACE_PANEL_Y = 0.4

/**
 * The source rect a panel of panelWidth x panelHeight shows for `face`: about 2.5 face widths, tall
 * enough that the face fills at most ~30% of the panel height, the face centre FACE_PANEL_Y down the
 * panel, never larger than the source and clamped inside it (no black edges).
 */
export function faceCropRect(face: SceneFace, panelWidth: number, panelHeight: number, source: SceneMetadata["source"]): SourceRect {
  const aspect = panelWidth / panelHeight
  const faceW = face.w_pct * source.width
  const faceH = face.h_pct * source.height
  const w = Math.min(Math.max(faceW * 2.5, faceH * 3.3 * aspect, source.width * 0.25), source.height * aspect, source.width)
  const h = w / aspect
  const x = Math.max(0, Math.min(source.width - w, face.x_pct * source.width - w / 2))
  const y = Math.max(0, Math.min(source.height - h, face.y_pct * source.height - h * FACE_PANEL_Y))
  return { x, y, w, h }
}

/** The single-mode crop: full source height at the output's aspect, centred on `centerXPct`. */
export function singleCropRect(centerXPct: number, outputAspect: number, source: SceneMetadata["source"]): SourceRect {
  const w = Math.min(source.width, source.height * outputAspect)
  return { x: Math.max(0, Math.min(source.width - w, centerXPct * source.width - w / 2)), y: 0, w, h: source.height }
}

/** A panel holds a face when at least this share of the face box is inside its crop... */
export const MIN_FACE_INSIDE = 0.8
/** ...in at least this share of the scene; below it the layout would show an empty panel. */
export const MIN_PANEL_COVERAGE = 0.9

function boxRect(face: SceneFace, source: SceneMetadata["source"]): SourceRect {
  const w = face.w_pct * source.width
  const h = face.h_pct * source.height
  return { x: face.x_pct * source.width - w / 2, y: face.y_pct * source.height - h / 2, w, h }
}

/** The share of `box` inside `rect`. */
export function insideShare(box: SourceRect, rect: SourceRect): number {
  const iw = Math.max(0, Math.min(box.x + box.w, rect.x + rect.w) - Math.max(box.x, rect.x))
  const ih = Math.max(0, Math.min(box.y + box.h, rect.y + rect.h) - Math.max(box.y, rect.y))
  return box.w * box.h > 0 ? (iw * ih) / (box.w * box.h) : 0
}

/** The people a chunk's detection found, at `t`: its faces (tracked), else its single tracked/static face. */
function detectedFacesAt(chunk: SceneChunk, t: number): SceneFace[] {
  if (chunk.faces?.length) return chunk.faces.map((f) => faceAt(f, t))
  if (chunk.face_track?.length) return [keyframeAt(chunk.face_track, t)]
  if (chunk.face_count > 0 && chunk.face_center_x_pct !== undefined) return [{ ...DEFAULT_FACE, x_pct: chunk.face_center_x_pct }]
  return []
}

/**
 * For each face panel `mode` would draw for `chunk` (as detected, before the switch), the share of the
 * scene (sampled every 0.5 s) in which a detected person's face sits MIN_FACE_INSIDE inside that
 * panel's crop. Empty when the layout has no face panels or nothing was detected (nothing to check).
 */
export function panelFaceCoverage(chunk: SceneChunk, mode: LayoutMode, outputWidth: number, outputHeight: number, source: SceneMetadata["source"]): number[] {
  const rendered = withLayoutMode(chunk, mode)
  const rects = layoutPanelRects(rendered, outputWidth / outputHeight)
  if (!rects.length || !detectedFacesAt(chunk, chunk.start_sec).length) return []
  const times: number[] = []
  for (let t = chunk.start_sec + 0.25; t < chunk.end_sec; t += 0.5) times.push(t)
  if (!times.length) times.push((chunk.start_sec + chunk.end_sec) / 2)
  return rects.map((rect) => {
    const pw = rect.w * outputWidth
    const ph = rect.h * outputHeight
    const hits = times.filter((t) => {
      const crop = rect.faceIndex < 0
        ? singleCropRect(faceCenterXAt(rendered, t), outputWidth / outputHeight, source)
        : faceCropRect(faceAt(rendered.faces?.[rect.faceIndex] ?? DEFAULT_FACE, t), pw, ph, source)
      return detectedFacesAt(chunk, t).some((f) => insideShare(boxRect(f, source), crop) >= MIN_FACE_INSIDE)
    })
    return hits.length / times.length
  })
}
